import clsx from 'clsx';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Globe,
  Info,
  Languages,
  Loader2,
  Play,
  Send,
  Target,
} from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import {
  KeyMapping,
  LANGUAGES,
  NotTranslateFlag,
  ProcessedText,
} from '../../types/localization';
import { useError } from '../../context/ErrorContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useTranslation } from 'react-i18next';
import {
  createAITranslationJob,
  getJob,
  listJobs,
  downloadJobArtifactArchive,
  triggerJob,
  type Job,
  type JobStatus,
} from '../../api/client';
import { PANEL_BASE, panelPath } from '../../config/paths';
import { Link, Navigate } from 'react-router-dom';
import { Download, ExternalLink, Briefcase, ChevronRight as ChevronRightIcon } from 'lucide-react';

// iOS App Store'da gelir/getiri açısından en önde gelen ülke dilleri (Apple / raporlara göre).
const TOP_IOS_MARKET_LOCALES = [
  'en', 'zh', 'zh-tw', 'ja', 'de', 'fr', 'ko', 'it', 'es', 'pt-br', 'es-mx',
  'ru', 'hi', 'nl', 'tr', 'id', 'ar', 'th', 'vi', 'pl', 'pt', 'uk',
];

const AILocalization: React.FC = () => {
  const { t } = useTranslation();
  const [sourceText, setSourceText] = useState<string>('');
  const [sourceLanguage, setSourceLanguage] = useState<string>('en');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { showError: showErrorPopup } = useError();
  const hasDebugMode = useDebugMode();
  const [, setProcessedText] = useState<ProcessedText | null>(null);
  const [showKeyMappings, setShowKeyMappings] = useState<boolean>(false);
  const [showOptimizedText, setShowOptimizedText] = useState<boolean>(false);
  const [showNotTranslateFlags, setShowNotTranslateFlags] =
    useState<boolean>(false);
  const [lastJobId, setLastJobId] = useState<string | null>(null);
  const [lastJob, setLastJob] = useState<Job | null>(null);
  const [startingPendingJob, setStartingPendingJob] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState<boolean>(false);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [recentJobsLoading, setRecentJobsLoading] = useState<boolean>(false);

  // Text preprocessing fonksiyonları
  const preprocessText = (text: string): ProcessedText => {
    const originalText = text;

    // Trim ve gereksiz newline'ları temizle
    let processedText = text
      .trim()
      .replace(/\n\s*\n/g, '\n') // Çoklu newline'ları tek newline yap
      .replace(/^\s+|\s+$/gm, ''); // Her satırın başındaki ve sonundaki boşlukları temizle

    // #NOT_TRANSLATE flag'lerini tespit et ve kaydet
    const notTranslateFlags: NotTranslateFlag[] = [];
    const lines = processedText.split('\n');
    const processedLines: string[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // #NOT_TRANSLATE flag'ini kontrol et
      if (trimmedLine.includes('#NOT_TRANSLATE')) {
        const processedLine = trimmedLine
          .replace(/\s*#NOT_TRANSLATE\s*$/, '')
          .trim();

        notTranslateFlags.push({
          lineNumber: index + 1,
          originalLine: trimmedLine,
          processedLine: processedLine,
        });

        // #NOT_TRANSLATE flag'li satırları optimized text'e ekleme
        // Bu satırlar AI'ya gönderilmeyecek
      } else {
        processedLines.push(line);
      }
    });

    processedText = processedLines.join('\n');

    // Comment satırlarını temizle
    processedText = processedText
      .replace(/^\s*\/\/.*$/gm, '') // // ile başlayan satırları sil
      .replace(/^\s*#.*$/gm, '') // # ile başlayan satırları sil
      .replace(/^\s*\/\*[\s\S]*?\*\//gm, '') // /* */ comment bloklarını sil
      .replace(/^\s*<!--[\s\S]*?-->/gm, '') // <!-- --> HTML comment'lerini sil
      .replace(/\n\s*\n/g, '\n') // Comment silindikten sonra oluşan boş satırları temizle
      .trim(); // Son trim

    // Key-value format kontrolü
    const keyValueRegex = /^"([^"]+)"\s*=\s*"([^"]+)";?\s*$/gm;
    const matches = Array.from(processedText.matchAll(keyValueRegex));

    if (matches.length > 0) {
      // Key-value format tespit edildi
      const keyMappings: KeyMapping[] = [];
      let processedWithNumericKeys = processedText;

      matches.forEach((match, index) => {
        const originalKey = match[1];
        const value = match[2];
        const numericKey = `${index}`; // 0, 1, 2, 3... şeklinde

        keyMappings.push({
          originalKey,
          numericKey,
          value,
        });

        // Orijinal key'i numeric key ile değiştir
        processedWithNumericKeys = processedWithNumericKeys.replace(
          match[0],
          `"${numericKey}" = "${value}";`
        );
      });

      return {
        originalText,
        processedText: processedWithNumericKeys,
        keyMappings,
        isKeyValueFormat: true,
        notTranslateFlags,
      };
    }

    return {
      originalText,
      processedText,
      keyMappings: [],
      isKeyValueFormat: false,
      notTranslateFlags,
    };
  };

  const restoreProcessedText = (
    translatedText: string,
    keyMappings: KeyMapping[],
    notTranslateFlags: NotTranslateFlag[]
  ): string => {
    // Text'i satırlara böl
    const lines = translatedText.split('\n');
    const restoredLines: string[] = [];

    // Her satırı ayrı ayrı işle
    lines.forEach(line => {
      let restoredLine = line;

      // Numeric key'leri orijinal key'lerle değiştir
      if (keyMappings.length > 0) {
        keyMappings.forEach(mapping => {
          const numericKeyPattern = new RegExp(`"${mapping.numericKey}"`, 'g');
          restoredLine = restoredLine.replace(
            numericKeyPattern,
            `"${mapping.originalKey}"`
          );
        });
      }

      restoredLines.push(restoredLine);
    });

    // #NOT_TRANSLATE flag'lerini geri ekle
    if (notTranslateFlags.length > 0) {
      // #NOT_TRANSLATE satırları optimized text'te olmadığı için
      // bunları çeviri sonrasına eklememiz gerekiyor
      const notTranslateLines = notTranslateFlags
        .sort((a, b) => a.lineNumber - b.lineNumber)
        .map(flag => flag.originalLine);

      // #NOT_TRANSLATE satırlarını listenin en üstüne ekle
      return notTranslateLines.join('\n') + '\n' + restoredLines.join('\n');
    }

    return restoredLines.join('\n');
  };

  const detectBrowserLanguage = (): string => {
    const browserLang = navigator.language || (navigator as any).userLanguage;

    if (!browserLang) return 'en';

    // Tam dil kodu (örn: 'tr-TR') varsa sadece dil kısmını al (örn: 'tr')
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Desteklenen diller listesinde var mı kontrol et
    const supportedLang = LANGUAGES.find(lang => lang.code === langCode);

    return supportedLang ? langCode : 'en';
  };

  // Component mount olduğunda browser dilini ayarla
  useEffect(() => {
    const detectedLang = detectBrowserLanguage();
    setSourceLanguage(detectedLang);
  }, []);

  // Sayfa yüklendiğinde kullanıcının aktif (pending/processing) ai_translation job'ını bul ve göster
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pending, processing] = await Promise.all([
          listJobs('pending'),
          listJobs('processing'),
        ]);
        if (cancelled) return;
        const all = [...pending, ...processing];
        const aiJobs = all.filter(j => j.type === 'ai_translation');
        if (aiJobs.length === 0) return;
        const sorted = aiJobs.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const active = sorted[0];
        setLastJobId(active.id);
        setLastJob(active);
      } catch {
        if (!cancelled) {
          // Sessizce bırak; liste sayfa için kritik değil
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Poll job status when we have a pending/processing job
  useEffect(() => {
    if (!lastJobId || !lastJob) return;
    if (lastJob.status !== 'pending' && lastJob.status !== 'processing') return;
    const t = setInterval(async () => {
      try {
        const job = await getJob(lastJobId);
        setLastJob(job);
      } catch {
        clearInterval(t);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [lastJobId, lastJob?.status]);

  const loadRecentJobs = useCallback(async () => {
    setRecentJobsLoading(true);
    try {
      const all = await listJobs();
      const aiJobs = all
        .filter((j: Job) => j.type === 'ai_translation')
        .sort(
          (a: Job, b: Job) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, 10);
      setRecentJobs(aiJobs);
    } catch {
      setRecentJobs([]);
    } finally {
      setRecentJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecentJobs();
  }, [loadRecentJobs]);

  const JOB_TYPE_KEYS: Record<string, string> = {
    ai_translation: 'panel.jobTypeAITranslation',
  };
  const STATUS_KEYS: Record<JobStatus, string> = {
    pending: 'panel.statusPending',
    processing: 'panel.statusProcessing',
    paused: 'panel.statusPaused',
    completed: 'panel.statusCompleted',
    failed: 'panel.statusFailed',
  };
  const STATUS_COLORS: Record<JobStatus, string> = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    paused: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };

  const handleSourceLanguageChange = (languageCode: string) => {
    setSourceLanguage(languageCode);
    // Remove source language from target languages if it was selected
    setTargetLanguages(prev => prev.filter(lang => lang !== languageCode));
  };

  const handleTargetLanguageToggle = (languageCode: string) => {
    if (languageCode === sourceLanguage) return; // Can't select source as target

    setTargetLanguages(prev => {
      if (prev.includes(languageCode)) {
        return prev.filter(lang => lang !== languageCode);
      } else {
        return [...prev, languageCode];
      }
    });
  };

  const handleSelectAllTargetLanguages = () => {
    const availableLanguages = LANGUAGES.filter(
      lang => lang.code !== sourceLanguage
    ).map(lang => lang.code);
    setTargetLanguages(availableLanguages);
  };

  const handleUnselectAllTargetLanguages = () => {
    setTargetLanguages([]);
  };

  const handleSelectTopIosMarketLanguages = () => {
    const availableCodes = new Set(LANGUAGES.map(l => l.code));
    const topCodes = TOP_IOS_MARKET_LOCALES.filter(
      code => code !== sourceLanguage && availableCodes.has(code)
    );
    setTargetLanguages(topCodes);
  };

  // Token hesaplama fonksiyonu
  const calculateTokens = (text: string): number => {
    // Basit token hesaplama: kelime sayısı + özel karakterler
    // Gerçek tokenizer kullanılabilir ama şimdilik basit yaklaşım
    const words = text
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0);
    const specialChars = text.match(/[^\w\s]/g) || [];
    return words.length + specialChars.length;
  };

  // Token bilgilerini hesapla
  const calculateTokenInfo = (
    optimizedText: string,
    targetLanguages: string[]
  ) => {
    const inputTokens = calculateTokens(optimizedText);
    const outputTokens = Math.ceil(inputTokens * targetLanguages.length * 1.5);
    const totalTokens = inputTokens + outputTokens;

    return {
      inputTokens,
      outputTokens,
      totalTokens,
    };
  };

  // Maliyet hesaplama fonksiyonu
  const calculateCost = (inputTokens: number, outputTokens: number) => {
    // Input: $0.05 per million tokens
    const inputCost = (inputTokens / 1000000) * 0.05;

    // Output: $0.40 per million tokens
    const outputCost = (outputTokens / 1000000) * 0.4;

    const totalCost = inputCost + outputCost;

    return {
      inputCost,
      outputCost,
      totalCost,
    };
  };

  const handleTranslate = async () => {
    if (!sourceText.trim() || targetLanguages.length === 0) {
      const msg = t('aiLocalizationTool.enterTextAndSelectLanguage');
      setError(msg);
      showErrorPopup(msg);
      return;
    }

    setIsTranslating(true);
    setError(null);

    try {
      const { jobId } = await createAITranslationJob({
        content: sourceText,
        sourceLocale: sourceLanguage,
        targetLocales: targetLanguages,
      });
      setLastJobId(jobId);
      const job = await getJob(jobId);
      setLastJob(job);
      loadRecentJobs();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('aiLocalizationTool.jobStartFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleStartPendingJob = async () => {
    if (!lastJobId || !lastJob || lastJob.status !== 'pending') return;
    setStartingPendingJob(true);
    setError(null);
    try {
      await triggerJob(lastJobId);
      const job = await getJob(lastJobId);
      setLastJob(job);
      loadRecentJobs();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('panel.translationJobStartFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setStartingPendingJob(false);
    }
  };

  if (!hasDebugMode) {
    return <Navigate to={PANEL_BASE} replace />;
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <Languages className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('aiLocalizationTool.title')}
          </h2>
        </div>
      </div>

      {/* Translation job status: link + download zip */}
      {lastJobId && lastJob && (
        <div
          className={
            lastJob.status === 'failed'
              ? 'mx-4 mt-2 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
              : lastJob.status === 'completed'
                ? 'mx-4 mt-2 p-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                : 'mx-4 mt-2 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
          }
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                {t('panel.jobTypeAITranslation')}:
              </span>
              <span className="text-sm text-amber-800 dark:text-amber-200">
                {lastJob.status === 'processing' && (
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-4 w-4 animate-spin inline" />
                    {t('panel.translationJobInProgress')}
                    {lastJob.progress != null ? ` (${lastJob.progress}%)` : ''}
                  </span>
                )}
                {lastJob.status === 'pending' && t('panel.translationJobPending')}
                {lastJob.status === 'completed' && (
                  <span className="text-green-800 dark:text-green-200">
                    {t('panel.translationJobCompleted')}
                  </span>
                )}
                {lastJob.status === 'failed' && (
                  <span className="text-red-600 dark:text-red-400">
                    {t('panel.translationJobFailed')}
                    {lastJob.errorMessage ? ` — ${lastJob.errorMessage}` : ''}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {lastJob.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => void handleStartPendingJob()}
                  disabled={startingPendingJob}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {startingPendingJob ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {t('panel.start')}
                </button>
              )}
              <Link
                to={panelPath(`/jobs/${lastJobId}`)}
                className="inline-flex items-center gap-1 text-sm text-amber-800 dark:text-amber-200 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {t('panel.jobDetailLink')}
              </Link>
              {lastJob.status === 'completed' && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowDownloadMenu(v => !v)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-600 text-white text-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
                  >
                    <Download className="h-4 w-4" />
                    {t('aiLocalizationTool.downloadZip')}
                  </button>
                  {showDownloadMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        aria-hidden
                        onClick={() => setShowDownloadMenu(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 z-20 py-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg min-w-[10rem]">
                        <p className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                          {t('aiLocalizationTool.deviceType')}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            downloadJobArtifactArchive(lastJobId!, 'localization-ios.zip', { format: 'ios' });
                            setShowDownloadMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          iOS ({' '}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            .lproj/Localizable.strings
                          </span>
                          )
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {lastJob.subProgress && lastJob.subProgress.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-2">
                {t('aiLocalizationTool.targetLocales')}
              </p>
              <ul className="space-y-1.5 max-h-24 overflow-y-auto pr-1">
                {lastJob.subProgress.map((sub) => {
                  const isDone = sub.status === 'completed';
                  const isFailed = sub.status === 'failed';
                  return (
                    <li key={sub.label} className="flex items-center gap-2">
                      <span className="w-12 text-xs font-medium text-amber-800 dark:text-amber-200 shrink-0">
                        {sub.label || '—'}
                      </span>
                      <div className="flex-1 min-w-0 h-1.5 rounded-full bg-amber-200/70 dark:bg-amber-900/50 overflow-hidden">
                        <div
                          className="h-full bg-amber-500 dark:bg-amber-400 rounded-full transition-all"
                          style={{ width: `${sub.progress ?? 0}%` }}
                        />
                      </div>
                      <span
                        className={`shrink-0 text-xs ${isDone ? 'text-green-600 dark:text-green-400' : isFailed ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'}`}
                      >
                        {isDone ? '✓' : isFailed ? '✗' : `${sub.progress ?? 0}%`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Input */}
        <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Source Text Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('aiLocalizationTool.sourceText')}
            </label>
            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-gray-400 dark:text-gray-500" />
              <span>{t('onboarding.aiLocalization.notTranslateHint')}</span>
            </p>
            <textarea
              value={sourceText}
              onChange={e => setSourceText(e.target.value)}
              placeholder={t('aiLocalizationTool.sourceTextPlaceholder')}
              className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />

            {/* Preprocessing Status */}
            {sourceText.trim() && (
              <div className="mt-2">
                {(() => {
                  const processed = preprocessText(sourceText);
                  return (
                    <div className="text-xs space-y-1">
                      {processed.isKeyValueFormat ? (
                        <div className="space-y-2">
                          <button
                            onClick={() => setShowKeyMappings(!showKeyMappings)}
                            className="flex items-center space-x-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                          >
                            <Check className="h-3 w-3" />
                            <span>
                              {t('aiLocalizationTool.keyValueFormatDetected', { count: processed.keyMappings.length })}
                            </span>
                            {showKeyMappings ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </button>

                          {showKeyMappings &&
                            processed.keyMappings.length > 0 && (
                              <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-xs">
                                <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {t('aiLocalizationTool.keyMappings')}
                                </div>
                                <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                                  {processed.keyMappings.map(
                                    (mapping, index) => (
                                      <div
                                        key={index}
                                        className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 py-0.5"
                                      >
                                        <span className="font-mono text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded flex-shrink-0">
                                          {mapping.numericKey}
                                        </span>
                                        <span className="flex-shrink-0">→</span>
                                        <span className="font-mono text-xs truncate">
                                          "{mapping.originalKey}"
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                          <Globe className="h-3 w-3" />
                          <span>{t('aiLocalizationTool.plainTextFormat')}</span>
                        </div>
                      )}

                      {/* #NOT_TRANSLATE Flags Toggle */}
                      {processed.notTranslateFlags.length > 0 && (
                        <div className="space-y-2">
                          <button
                            onClick={() =>
                              setShowNotTranslateFlags(!showNotTranslateFlags)
                            }
                            className="flex items-center space-x-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors"
                          >
                            <AlertCircle className="h-3 w-3" />
                            <span>
                              {t('aiLocalizationTool.notTranslateFlagsCount', { count: processed.notTranslateFlags.length })}
                            </span>
                            {showNotTranslateFlags ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </button>

                          {showNotTranslateFlags && (
                            <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-xs">
                              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('aiLocalizationTool.notTranslateFlagsLabel')}
                              </div>
                              <div className="max-h-32 overflow-y-auto space-y-1">
                                {processed.notTranslateFlags.map(
                                  (flag, index) => (
                                    <div
                                      key={index}
                                      className="flex items-start space-x-2 text-gray-600 dark:text-gray-400"
                                    >
                                      <span className="font-mono text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 px-1 rounded flex-shrink-0">
                                        L{flag.lineNumber}
                                      </span>
                                      <span className="font-mono text-xs truncate">
                                        {flag.processedLine}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Optimized Text Toggle */}
                      {processed.processedText !== processed.originalText && (
                        <div className="space-y-2">
                          <button
                            onClick={() =>
                              setShowOptimizedText(!showOptimizedText)
                            }
                            className="flex items-center space-x-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                          >
                            <Target className="h-3 w-3" />
                            <span>{t('aiLocalizationTool.optimizedText')}</span>
                            {showOptimizedText ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </button>

                          {showOptimizedText && (
                            <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-xs">
                              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('aiLocalizationTool.optimizedTextSentToAI')}
                              </div>
                              <div className="bg-gray-900 text-gray-100 p-2 rounded font-mono text-xs whitespace-pre-wrap max-h-32 overflow-y-auto">
                                {processed.processedText}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Language Selection */}
          <div className="p-4 space-y-4">
            {/* Source Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Globe className="inline h-4 w-4 mr-1" />
                {t('aiLocalizationTool.sourceLanguage')}
                {sourceLanguage === detectBrowserLanguage() && (
                  <span className="ml-2 text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 px-2 py-1 rounded">
                    {t('aiLocalizationTool.autoDetected')}
                  </span>
                )}
              </label>
              <select
                value={sourceLanguage}
                onChange={e => handleSourceLanguageChange(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name} ({lang.nativeName})
                    {lang.code === detectBrowserLanguage() &&
                      ` - ${t('aiLocalizationTool.browserLanguageSuffix')}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Target Languages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Target className="inline h-4 w-4 mr-1" />
                  {t('aiLocalizationTool.targetLanguagesCount', { count: targetLanguages.length })}
                </label>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handleSelectAllTargetLanguages}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                  >
                    {t('panel.selectAll')}
                  </button>
                  <button
                    onClick={handleSelectTopIosMarketLanguages}
                    className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30 rounded transition-colors"
                    title={t('panel.aiTranslationSelectTopIosMarkets')}
                  >
                    {t('panel.aiTranslationSelectTopIosMarkets')}
                  </button>
                  <button
                    onClick={handleUnselectAllTargetLanguages}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    {t('aiLocalizationTool.unselectAll')}
                  </button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 p-2">
                {LANGUAGES.filter(lang => lang.code !== sourceLanguage).map(
                  lang => (
                    <label
                      key={lang.code}
                      className={clsx(
                        'flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                        targetLanguages.includes(lang.code) &&
                          'bg-primary-50 dark:bg-primary-900/20'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={targetLanguages.includes(lang.code)}
                        onChange={() => handleTargetLanguageToggle(lang.code)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-white">
                        {lang.flag} {lang.name} ({lang.nativeName})
                      </span>
                    </label>
                  )
                )}
              </div>
            </div>

            {/* Token Information */}
            {sourceText.trim() && targetLanguages.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('aiLocalizationTool.estimatedTokenUsage')}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const processed = preprocessText(sourceText);
                      const tokens = calculateTokenInfo(
                        processed.processedText,
                        targetLanguages
                      );
                      return (
                        <>
                          <span className="bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-xs">
                            {t('aiLocalizationTool.inputLabel')}: {tokens.inputTokens}
                          </span>
                          <span className="bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs">
                            {t('aiLocalizationTool.outputLabel')}: {tokens.outputTokens}
                          </span>
                          <span className="bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-2 py-1 rounded text-xs">
                            {t('aiLocalizationTool.totalLabel')}: {tokens.totalTokens}
                          </span>
                        </>
                      );
                    })()}
                  </div>

                  {/* Cost Information */}
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div className="flex items-center justify-between">
                      <span>{t('aiLocalizationTool.inputCost')}:</span>
                      <span className="font-mono">
                        {(() => {
                          const processed = preprocessText(sourceText);
                          const tokens = calculateTokenInfo(
                            processed.processedText,
                            targetLanguages
                          );
                          const cost = calculateCost(
                            tokens.inputTokens,
                            tokens.outputTokens
                          );
                          return `$${cost.inputCost.toFixed(6)}`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{t('aiLocalizationTool.outputCost')}:</span>
                      <span className="font-mono">
                        {(() => {
                          const processed = preprocessText(sourceText);
                          const tokens = calculateTokenInfo(
                            processed.processedText,
                            targetLanguages
                          );
                          const cost = calculateCost(
                            tokens.inputTokens,
                            tokens.outputTokens
                          );
                          return `$${cost.outputCost.toFixed(6)}`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-1">
                      <span className="font-medium">{t('aiLocalizationTool.totalCost')}:</span>
                      <span className="font-mono font-medium">
                        {(() => {
                          const processed = preprocessText(sourceText);
                          const tokens = calculateTokenInfo(
                            processed.processedText,
                            targetLanguages
                          );
                          const cost = calculateCost(
                            tokens.inputTokens,
                            tokens.outputTokens
                          );
                          return `$${cost.totalCost.toFixed(6)}`;
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Pricing Info */}
                  <div className="text-xs text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 p-2 rounded">
                    <div className="space-y-1">
                      <div>
                        {t('aiLocalizationTool.inputPricingInfo')}
                      </div>
                      <div>
                        {t('aiLocalizationTool.outputPricingInfo')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Translate Button */}
            <button
              onClick={handleTranslate}
              disabled={
                !sourceText.trim() ||
                targetLanguages.length === 0 ||
                isTranslating
              }
              className={clsx(
                'w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors',
                isTranslating ||
                  !sourceText.trim() ||
                  targetLanguages.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
              )}
            >
              {isTranslating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('aiLocalizationTool.translating')}</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>{t('aiLocalizationTool.translate')}</span>
                </>
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Son 10 AI Translation Job */}
        <div className="w-1/2 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('panel.recentAITranslationJobs')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {t('panel.recentAITranslationJobsDescription')}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {recentJobsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : recentJobs.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('panel.noRecentAITranslationJobs')}</p>
                <p className="text-sm mt-1">
                  {t('panel.noRecentAITranslationJobsHint')}
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {recentJobs.map((job) => (
                  <li key={job.id}>
                    <Link
                      to={panelPath(`/jobs/${job.id}`)}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <Briefcase className="h-5 w-5 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {t(JOB_TYPE_KEYS[job.type] ?? job.type)}
                          </span>
                          <span
                            className={clsx(
                              'text-xs font-medium px-2 py-0.5 rounded shrink-0',
                              STATUS_COLORS[job.status]
                            )}
                          >
                            {t(STATUS_KEYS[job.status])}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {new Date(job.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-primary-500 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AILocalization;
